import { Outlet } from "react-router-dom";

import { useAuth } from "../hooks/useAuth.js";
import { IssuePanelProvider } from "../contexts/IssuePanelContext.js";
import { PageBackProvider } from "../context/PageBackContext.js";
import { PolicySignGate } from "./PolicySignGate.js";
import { SidebarNav } from "./SidebarNav.js";
import { TopHeaderBar } from "./TopHeaderBar.js";
import { NotificationToast } from "./notifications/NotificationToast.js";

function decodeUser(token: string): { email: string; role: string } {
  try {
    const p = JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>;
    return { email: String(p["email"] ?? ""), role: String(p["role"] ?? "") };
  } catch {
    return { email: "", role: "" };
  }
}

export function AppShell() {
  const { accessToken } = useAuth();
  const user = accessToken ? decodeUser(accessToken) : { email: "", role: "" };

  return (
    <IssuePanelProvider>
      <PageBackProvider>
        <div className="flex h-screen overflow-hidden">
          <SidebarNav email={user.email} role={user.role} />

          {/* overscroll-y-none: without it, swiping down at the top rubber-bands
              this container past its own top edge, dragging the sticky
              TopHeaderBar down and leaving a blank strip above it. It also stops
              the scroll chaining out to the document. */}
          {/* scrollbar-gutter: stable -- the gutter is reserved whether or not a
              scrollbar is showing. Without it, any content that grows past the
              viewport (an inline panel opening, a list loading) makes the
              scrollbar appear mid-animation, which narrows this container and
              reflows everything inside it sideways in one frame: the stutter
              reads as the page "sticking" while it slides. */}
          <main className="flex min-w-0 flex-1 flex-col overflow-y-auto overscroll-y-none bg-surface-50 [scrollbar-gutter:stable]">
            <TopHeaderBar />
            <PolicySignGate />
            {/* Fills the height left over by the header/banner, so a page asking for
                height: 100% gets the space that is actually free rather than the full
                100vh main is tall — otherwise every full-height page overflows by the
                header's height and its pinned footer falls below the fold. Pages taller
                than this still overflow visibly and main still scrolls them. */}
            <div className="flex min-h-0 flex-1 flex-col">
              <Outlet />
            </div>
          </main>

          {/* One toast for the whole app, driven by the store's SSE arrivals. */}
          <NotificationToast />
        </div>
      </PageBackProvider>
    </IssuePanelProvider>
  );
}
